import type { EditorProps } from '@monaco-editor/react';
import { Component, lazy, Suspense, type ReactNode } from 'react';
import styled from 'styled-components';

const LazyMonacoEditor = lazy(() => import('./LocalMonacoEditor'));

export type MonacoEditorProps = EditorProps;

/** Error-boundary `<pre>` only; Json modal and admin editors pass string JSON via value/defaultValue. */
function editorFallbackText(props: MonacoEditorProps): string | undefined {
  const content = props.value ?? props.defaultValue;
  return typeof content === 'string' ? content : undefined;
}

export function MonacoEditor(props: MonacoEditorProps) {
  const fallbackText = editorFallbackText(props);

  return (
    <Suspense fallback={<LoadingFallback>{props.loading ?? 'Loading...'}</LoadingFallback>}>
      <MonacoEditorErrorBoundary fallbackText={fallbackText}>
        <LazyMonacoEditor {...props} />
      </MonacoEditorErrorBoundary>
    </Suspense>
  );
}

type MonacoEditorErrorBoundaryProps = {
  children: ReactNode;
  fallbackText?: string;
};

type MonacoEditorErrorBoundaryState = {
  hasError: boolean;
};

class MonacoEditorErrorBoundary extends Component<MonacoEditorErrorBoundaryProps, MonacoEditorErrorBoundaryState> {
  override state: MonacoEditorErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): MonacoEditorErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidUpdate(previousProps: MonacoEditorErrorBoundaryProps) {
    if (this.state.hasError && previousProps.fallbackText !== this.props.fallbackText) {
      this.setState({ hasError: false });
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <EditorErrorFallback>
          <EditorErrorMessage>Editor failed to load.</EditorErrorMessage>
          {this.props.fallbackText != null ? <EditorErrorContent>{this.props.fallbackText}</EditorErrorContent> : null}
        </EditorErrorFallback>
      );
    }

    return this.props.children;
  }
}

const LoadingFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const EditorErrorFallback = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing8}px;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const EditorErrorMessage = styled.div`
  flex-shrink: 0;
`;

const EditorErrorContent = styled.pre`
  flex: 1;
  margin: 0;
  overflow: auto;
  min-height: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;
